import {IProject} from "../models/interfaces/project.interface.ts";
import {IStateEnum, mapStateDtoToEnum} from "../models/enums/state.enum.ts";
import {IItemProgress} from "../models/interfaces/itemProgress.interface.ts";
import {IGroupProgress} from "../models/interfaces/groupProgress.interface.ts";
import {useColorMode} from "@chakra-ui/react";
import {IProjectStateStyle} from "../models/interfaces/projectStateStyle.interface.ts";

/**
 * Hook to manage pagination logic.
 */
export const useProject = () => {
  const {colorMode} = useColorMode();

  const extractAllFeatureItems = (featuresStates: IProject["featuresStates"]): IItemProgress[] => {
    const translations: IItemProgress[] = Object.values(featuresStates.subtitleTranslations || {});
    const descriptions: IItemProgress[] = Object.values(featuresStates.audioDescriptions || {});
    const dialogue: IItemProgress[] = featuresStates.dialogueList ? [featuresStates.dialogueList] : [];
    const metadata: IItemProgress[] = featuresStates.extendedMetadata ? [featuresStates.extendedMetadata] : [];
    return [...translations, ...descriptions, ...dialogue, ...metadata];
  };

  const extractAllFeatureItemsIncludeTranscription = (project: IProject): IItemProgress[] => {
    const allItems: IItemProgress[] = extractAllFeatureItems(project.featuresStates);
    const transcriptionItem: IItemProgress = {
      pct: project.transcription.status == "co" ? 100 : 0,
      state: project.transcription.status,
      updated_at: project.transcription.updatedAt,
      created_at: project.transcription.createdAt,
      downloaded: false,
    };
    return [transcriptionItem, ...allItems];
  }

  const isProjectDownloaded = (project: IProject): boolean => {
    const allItems: IItemProgress[] = extractAllFeatureItems(project.featuresStates);
    return allItems.every((item) => item.downloaded);
  };

  const extractTargetTranslationsLanguages = (project: IProject): string[] => {
    const translations: IGroupProgress | undefined = project.featuresStates.subtitleTranslations;
    if (!translations) return [];
    return Object.keys(translations)
  }

  const extractAudioDescriptionsLanguages = (project: IProject): string[] => {
    const audioDescriptions: IGroupProgress | undefined = project.featuresStates.audioDescriptions;
    if (!audioDescriptions) return [];
    return Object.keys(audioDescriptions)
  }

  const getDateStringAtUtcFormat = (dateString: string): string => {
    // Normalize the UTC string to have exactly 3 digits for milliseconds and one trailing 'Z'
    const trimmed: string = dateString.replace(/(\.\d{3})\d{3}/, "$1").replace(/Z$/, "") + "Z";
    const utcDate = new Date(trimmed);

    // Detect user's local timezone
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Format the date in the desired format
    const formatted = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(utcDate);

    return formatted.replace(",", " ").replace(",", " At");
  }

  // Converts StateDto to IStateEnum using the utility function
  const getItemStateEnum = (item: IItemProgress): IStateEnum => {
    return mapStateDtoToEnum(item.state);
  };

  const getGroupStateEnum = (group: IGroupProgress): IStateEnum => {
    const items: IItemProgress[] = Object.values(group);
    const states: IStateEnum[] = items.map((item) => mapStateDtoToEnum(item.state));

    if (states.every((s: IStateEnum) => s === IStateEnum.PENDING)) return IStateEnum.PENDING;
    if (states.every((s: IStateEnum) => s === IStateEnum.DONE)) return IStateEnum.DONE;
    if (states.some((s: IStateEnum) => s === IStateEnum.FAILED)) return IStateEnum.FAILED;
    return IStateEnum.IN_PROGRESS;
  };

  // Returns the aggregated state for a project
  const getProjectStateEnum = (project: IProject): IStateEnum => {
    const transcriptionState: IStateEnum = mapStateDtoToEnum(project.transcription.status);
    const allItems: IItemProgress[] = extractAllFeatureItems(project.featuresStates);
    const states: IStateEnum[] = allItems.map((item: IItemProgress) => mapStateDtoToEnum(item.state));
    states.unshift(transcriptionState)

    if (states.every((s: IStateEnum) => s === IStateEnum.PENDING)) return IStateEnum.PENDING;
    if (states.every((s: IStateEnum) => s === IStateEnum.DONE)) return IStateEnum.DONE;
    if (states.some((s: IStateEnum) => s === IStateEnum.FAILED)) return IStateEnum.FAILED;
    return IStateEnum.IN_PROGRESS;
  };

  const getStateTheme = (projectState: IStateEnum): IProjectStateStyle => {
    const isDarkMode: boolean = colorMode === "dark";

    switch (projectState) {
      case IStateEnum.PENDING:
        return {
          bgColor: isDarkMode ? "bg-brandPurple-400/70" : "bg-brandPurple-500",
          hoverBgColor: isDarkMode ? "hover:bg-brandPurple-500/70" : "hover:bg-brandPurple-600",
          textColor: isDarkMode ? "text-n-200" : "text-n-600",
        };
      case IStateEnum.IN_PROGRESS:
        return {
          bgColor: isDarkMode ? "bg-sunsetAmber-400/70" : "bg-sunsetAmber-500",
          hoverBgColor: isDarkMode ? "hover:bg-sunsetAmber-500" : "hover:bg-sunsetAmber-600",
          textColor: isDarkMode ? "text-n-200" : "text-n-600",
        };
      case IStateEnum.DONE:
        return {
          bgColor: isDarkMode ? "bg-success-500/50" : "bg-success-500",
          hoverBgColor: isDarkMode ? "hover:bg-success-400/50" : "hover:bg-success-600",
          textColor: isDarkMode ? "text-n-200" : "text-n-600",
        };
      case IStateEnum.FAILED:
        return {
          bgColor: isDarkMode ? "bg-bloodCrimson-400/70" : "bg-bloodCrimson-500",
          hoverBgColor: isDarkMode ? "hover:bg-bloodCrimson-500" : "hover:bg-bloodCrimson-600",
          textColor: isDarkMode ? "text-n-200" : "text-n-600",
        };
      default:
        return {
          bgColor: isDarkMode ? "bg-ironGray-300/70" : "bg-ironGray-500",
          hoverBgColor: isDarkMode ? "hover:bg-ironGray-400" : "hover:bg-ironGray-600",
          textColor: isDarkMode ? "text-n-200" : "text-n-600",
        };
    }
  }

  const calculateGroupPercentage = (group: IGroupProgress): number | null => {
    const items: IItemProgress[] = Object.values(group);
    if (items.length === 0) return null;
    const totalPct: number = items.reduce((sum: number, item: IItemProgress) => sum + item.pct, 0);
    return Math.round(totalPct / items.length);
  };


  const calculateProjectPercentage = (project: IProject): number | null => {
    const allItems: IItemProgress[] = extractAllFeatureItemsIncludeTranscription(project);

    if (allItems.length === 0) return null;
    const totalPct: number = allItems.reduce((sum: number, item: IItemProgress) => sum + item.pct, 0);
    return Math.round(totalPct / allItems.length);
  };

  return {
    extractAllFeatureItems,
    extractAllFeatureItemsIncludeTranscription,
    isProjectDownloaded,
    extractTargetTranslationsLanguages,
    extractAudioDescriptionsLanguages,
    getDateStringAtUtcFormat,
    getItemStateEnum,
    getGroupStateEnum,
    getProjectStateEnum,
    getStateTheme,
    calculateGroupPercentage,
    calculateProjectPercentage,
  };
};